/* eslint-disable no-restricted-globals */
import {
    BreachEvent,
    ConfigurationContext,
    DataLayerEventName,
    getEncodedState,
    getOidcCallbackURI,
    RegistrationSessionDetails,
    retrieveCookie,
    startRegistration,
} from '@news-mono/web-common'
import React, { FormEvent, useContext, useState } from 'react'
import { LoggerContext } from '../../../../../diagnostics/LoggerContext'
import { TextLink } from '../../../../../typography/TextLink/TextLink'
import { useAuthActions } from '../../../../../user-registration/authentication/AuthenticationProvider'
import { useSectionMetaInfo } from '../../../../../__helpers/use-section-meta'
import { generateRegwallLink } from './helpers/generate-regwall-link'
import {
    LoginText,
    StyledButtonLink,
    StyledInput,
    StyledLabel,
    StyledRegisterButton,
    StyledRegistrationResponse,
    StyledRegistrationWallForm,
    StyledTermsPrivacyText,
} from './RegistrationWallForm.styled'

interface RegistrationWallFormProps {
    onEvent: (event: any) => void
    eventPayload: BreachEvent['payload']
}

interface RegistrationResponse {
    isEmailValid: boolean
}

export const registrationSuccessMessage =
    'Thanks for beginning your registration. So we can confirm your account, please check your email to complete the process.'

export const RegistrationWallForm: React.FC<RegistrationWallFormProps> = ({
    onEvent,
    eventPayload,
}) => {
    // Ensure this is set on render. and not in submit - to avoid event propagation (only used client-side)
    const [regwallArticleLink] = useState(
        typeof window !== 'undefined' ? window.location : null,
    )

    const config = useContext(ConfigurationContext)
    const logger = useContext(LoggerContext)
    const sectionMeta = useSectionMetaInfo()

    const [registrationResponse, setRegistrationResponse] = useState<
        RegistrationResponse | undefined
    >(undefined)
    const [isRegistering, setIsRegistering] = useState(false)

    const [email, setEmail] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const { onLoginClick } = useAuthActions()

    async function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        setIsRegistering(true)

        onEvent({
            type: DataLayerEventName.breachScreenClick,
            originator: 'breachscreen',
            payload: eventPayload,
        })

        const gaSessionIdCookie =
            (retrieveCookie('_ga') as unknown as string) || ''

        const regDetails: RegistrationSessionDetails = {
            email,
            honeypot,
            state: getEncodedState({
                originUrl: generateRegwallLink(
                    regwallArticleLink as Location,
                    gaSessionIdCookie,
                ),
                bypassStateCheck: true,
            }),
            client_id: config.auth?.clientId,
            redirect_uri: getOidcCallbackURI(location.origin, sectionMeta),
        }

        const hasRegistered = await startRegistration(
            config,
            logger,
            regDetails,
        )

        setRegistrationResponse({ isEmailValid: hasRegistered })
        setIsRegistering(false)
    }

    function retryForm() {
        setRegistrationResponse(undefined)
        setEmail('')
    }

    return (
        <StyledRegistrationWallForm
            data-testid="BreachScreen-Regwall"
            onSubmit={onSubmit}
        >
            <StyledLabel htmlFor="email">Email address</StyledLabel>
            <StyledInput
                name="email"
                type="email"
                placeholder="name@email.com.au"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!registrationResponse}
            />

            {/* Honeypot field, will be rejected by API if a value is entered */}
            <StyledInput
                hidden
                name="address"
                value={honeypot}
                type="text"
                onChange={(e) => setHoneypot(e.target.value)}
            />

            {registrationResponse ? (
                registrationResponse.isEmailValid ? (
                    <StyledRegistrationResponse>
                        {registrationSuccessMessage}
                    </StyledRegistrationResponse>
                ) : (
                    <StyledRegistrationResponse>
                        <StyledButtonLink onClick={retryForm}>
                            Submitted email is invalid, please try again.
                        </StyledButtonLink>
                    </StyledRegistrationResponse>
                )
            ) : (
                <React.Fragment>
                    <StyledTermsPrivacyText>
                        By clicking Register, you agree to the{' '}
                        <TextLink
                            href="https://thewest.com.au/terms"
                            openInNewWindow={true}
                        >
                            Terms of Use
                        </TextLink>{' '}
                        and{' '}
                        <TextLink
                            href="https://thewest.com.au/privacy-statement"
                            openInNewWindow={true}
                        >
                            Privacy&nbsp;Statement
                        </TextLink>
                        .
                    </StyledTermsPrivacyText>

                    <StyledRegisterButton
                        type="submit"
                        buttonWidth="wide"
                        disabled={isRegistering}
                    >
                        Register
                    </StyledRegisterButton>

                    <StyledButtonLink
                        onClick={() => onLoginClick('breach-screen')}
                    >
                        Already registered? <LoginText>Login</LoginText>
                    </StyledButtonLink>
                </React.Fragment>
            )}
        </StyledRegistrationWallForm>
    )
}
RegistrationWallForm.displayName = 'RegistrationWallForm'
